import { requestJson } from "@/shared/services/api";
import type {
  PublishedCaseCard,
  PublishedCaseDetail,
} from "../types/content.types";
import type { Locale } from "@/shared/i18n/locale";

export interface PublicCaseMetricResponse {
  value: string;
  prefix?: string | null;
  label: string;
}

export interface PublicCaseCardResponse {
  id: string;
  client: string;
  logo: string;
  logo_alt?: string;
  tags: string[];
  title: string;
  metrics: PublicCaseMetricResponse[];
  screenshot: string;
  screenshot_alt?: string;
  screenshot_position_x?: number;
  screenshot_position_y?: number;
  screenshot_zoom?: number;
  slug: string;
}

export interface PublicCaseDetailResponse extends PublicCaseCardResponse {
  excerpt: string;
  body_markdown: string;
  seo_title: string;
  seo_description: string;
  first_published_at: string | null;
  published_at: string | null;
  updated_at: string | null;
  translation_available?: boolean;
  original_href?: string | null;
}

export function toCaseCard(caseEntry: PublicCaseCardResponse): PublishedCaseCard {
  return {
    id: caseEntry.id,
    client: caseEntry.client,
    logo: caseEntry.logo,
    logoAlt: caseEntry.logo_alt || caseEntry.client,
    tags: caseEntry.tags,
    title: caseEntry.title,
    metrics: caseEntry.metrics.map((metric) => ({
      value: metric.value,
      prefix: metric.prefix ?? undefined,
      label: metric.label,
    })),
    screenshot: caseEntry.screenshot,
    screenshotAlt: caseEntry.screenshot_alt || caseEntry.title,
    screenshotPosition: {
      x: caseEntry.screenshot_position_x ?? 50,
      y: caseEntry.screenshot_position_y ?? 50,
      zoom: caseEntry.screenshot_zoom ?? 100,
    },
    slug: caseEntry.slug,
  };
}

export function toCaseDetail(caseEntry: PublicCaseDetailResponse): PublishedCaseDetail {
  return {
    ...toCaseCard(caseEntry),
    excerpt: caseEntry.excerpt,
    bodyMarkdown: caseEntry.body_markdown,
    seoTitle: caseEntry.seo_title,
    seoDescription: caseEntry.seo_description,
    firstPublishedAt: caseEntry.first_published_at,
    publishedAt: caseEntry.published_at,
    updatedAt: caseEntry.updated_at,
    translationAvailable: caseEntry.translation_available,
    originalHref: caseEntry.original_href ?? null,
  };
}

export async function getPublishedCases(
  locale?: Locale,
  signal?: AbortSignal,
): Promise<PublishedCaseCard[]> {
  const query = locale === "en" ? "?locale=en" : "";
  const cases = await requestJson<PublicCaseCardResponse[]>(
    `/api/public/cases${query}`,
    { signal, cache: "no-cache" },
  );
  return cases.map(toCaseCard);
}

export async function getPublishedCaseBySlug(
  slug: string,
  signal?: AbortSignal,
  locale?: Locale,
): Promise<PublishedCaseDetail> {
  const query = locale === "en" ? "?locale=en" : "";
  const caseEntry = await requestJson<PublicCaseDetailResponse>(
    `/api/public/cases/${slug}${query}`,
    { signal, cache: "no-cache" },
  );
  return toCaseDetail(caseEntry);
}
